import { useRef, useState, useEffect } from "react";
import {
  TextB, TextItalic, TextHOne, TextHTwo, TextHThree,
  ListBullets, ListNumbers, Quotes, Link as LinkIcon, Code, TextStrikethrough,
  TextAa, PaintBrush, TextT,
} from "@phosphor-icons/react";

const FONT_SIZES = [
  { label: "Small", value: "14px" },
  { label: "Normal", value: "16px" },
  { label: "Large", value: "20px" },
  { label: "XL", value: "26px" },
  { label: "XXL", value: "34px" },
];

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Serif (elegant)", value: "'Fraunces', ui-serif, Georgia, serif" },
  { label: "Sans-serif (clean)", value: "'IBM Plex Sans', system-ui, sans-serif" },
  { label: "Display (bold)", value: "'Cabinet Grotesk', system-ui, sans-serif" },
  { label: "Mono (code)", value: "ui-monospace, SFMono-Regular, monospace" },
];

const COLOR_PALETTE = [
  "#09090B", "#52525B", "#71717A", "#A1A1AA",
  "#DC2626", "#EA580C", "#D97706", "#CA8A04",
  "#16A34A", "#0891B2", "#2563EB", "#7C3AED",
  "#DB2777", "#BE185D", "#7F1D1D", "#166534",
];

/**
 * Markdown editor with a formatting toolbar including font size, family, and color.
 * Uses raw HTML <span> tags for advanced styling (rendered via rehype-raw on the public page).
 */
export default function MarkdownField({ value, onChange, minHeight = 160, placeholder = "", testid }) {
  const ref = useRef(null);
  const [openMenu, setOpenMenu] = useState(null); // 'size' | 'font' | 'color' | null

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest?.("[data-md-menu]")) setOpenMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const wrap = (before, after = before, hint = "text") => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end) || hint;
    const next = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + before.length + selected.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  const prefixLines = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    let lineEnd = value.indexOf("\n", end);
    if (lineEnd === -1) lineEnd = value.length;
    const chunk = value.substring(lineStart, lineEnd) || "text";
    const prefixed = chunk.split("\n").map((l) => (l ? prefix + l : l)).join("\n");
    onChange(value.substring(0, lineStart) + prefixed + value.substring(lineEnd));
  };

  const wrapSpan = (styleObj) => {
    const styleStr = Object.entries(styleObj).map(([k, v]) => `${k}:${v}`).join(";");
    wrap(`<span style="${styleStr}">`, `</span>`, "styled text");
    setOpenMenu(null);
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:", "https://");
    if (!url) return;
    wrap("[", `](${url})`, "link text");
  };

  const btn = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 30, height: 30, padding: "0 6px",
    border: "1px solid var(--border)", background: "#fff",
    cursor: "pointer", color: "var(--text)", fontFamily: "inherit",
    fontSize: 12, gap: 4, position: "relative",
  };

  const popover = {
    position: "absolute", top: "calc(100% + 4px)", left: 0,
    background: "#fff", border: "1px solid var(--border)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)", padding: 6, zIndex: 30,
    minWidth: 160,
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }} data-testid="md-toolbar">
        <button type="button" title="Heading 1" style={btn} onClick={() => prefixLines("# ")} data-testid="md-h1"><TextHOne size={16} weight="bold" /></button>
        <button type="button" title="Heading 2" style={btn} onClick={() => prefixLines("## ")} data-testid="md-h2"><TextHTwo size={16} weight="bold" /></button>
        <button type="button" title="Heading 3" style={btn} onClick={() => prefixLines("### ")} data-testid="md-h3"><TextHThree size={16} weight="bold" /></button>
        <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
        <button type="button" title="Bold" style={btn} onClick={() => wrap("**")} data-testid="md-bold"><TextB size={16} weight="bold" /></button>
        <button type="button" title="Italic" style={btn} onClick={() => wrap("*")} data-testid="md-italic"><TextItalic size={16} weight="bold" /></button>
        <button type="button" title="Strikethrough" style={btn} onClick={() => wrap("~~")} data-testid="md-strike"><TextStrikethrough size={16} weight="bold" /></button>
        <button type="button" title="Inline code" style={btn} onClick={() => wrap("`")} data-testid="md-code"><Code size={16} weight="bold" /></button>
        <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />

        {/* Font size */}
        <div data-md-menu style={{ position: "relative" }}>
          <button type="button" title="Font size" style={btn}
            onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === "size" ? null : "size"); }}
            data-testid="md-size"><TextAa size={16} weight="bold" /> ▾</button>
          {openMenu === "size" && (
            <div style={popover}>
              {FONT_SIZES.map((s) => (
                <button key={s.value} type="button" onClick={() => wrapSpan({ "font-size": s.value })}
                  style={{ display: "block", width: "100%", padding: "6px 10px", border: 0, background: "transparent",
                           textAlign: "left", cursor: "pointer", fontSize: s.value === "14px" ? 12 : s.value === "16px" ? 14 : 16 }}
                  data-testid={`md-size-${s.label.toLowerCase()}`}>{s.label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Font family */}
        <div data-md-menu style={{ position: "relative" }}>
          <button type="button" title="Font family" style={btn}
            onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === "font" ? null : "font"); }}
            data-testid="md-font"><TextT size={16} weight="bold" /> ▾</button>
          {openMenu === "font" && (
            <div style={{ ...popover, minWidth: 200 }}>
              {FONT_FAMILIES.map((f) => (
                <button key={f.label} type="button"
                  onClick={() => f.value ? wrapSpan({ "font-family": f.value }) : wrap("", "", "text")}
                  style={{ display: "block", width: "100%", padding: "6px 10px", border: 0, background: "transparent",
                           textAlign: "left", cursor: "pointer", fontFamily: f.value || "inherit", fontSize: 14 }}
                  data-testid={`md-font-${f.label.split(" ")[0].toLowerCase()}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Color */}
        <div data-md-menu style={{ position: "relative" }}>
          <button type="button" title="Text color" style={btn}
            onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === "color" ? null : "color"); }}
            data-testid="md-color"><PaintBrush size={16} weight="bold" /> ▾</button>
          {openMenu === "color" && (
            <div style={{ ...popover, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, padding: 8 }}>
              {COLOR_PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => wrapSpan({ color: c })}
                  title={c}
                  style={{ width: 26, height: 26, border: "1px solid var(--border)", background: c, cursor: "pointer", borderRadius: 2 }}
                  data-testid={`md-color-${c.substring(1).toLowerCase()}`} />
              ))}
            </div>
          )}
        </div>

        <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
        <button type="button" title="Bulleted list" style={btn} onClick={() => prefixLines("- ")} data-testid="md-ul"><ListBullets size={16} weight="bold" /></button>
        <button type="button" title="Numbered list" style={btn} onClick={() => prefixLines("1. ")} data-testid="md-ol"><ListNumbers size={16} weight="bold" /></button>
        <button type="button" title="Blockquote" style={btn} onClick={() => prefixLines("> ")} data-testid="md-quote"><Quotes size={16} weight="bold" /></button>
        <button type="button" title="Link" style={btn} onClick={insertLink} data-testid="md-link"><LinkIcon size={16} weight="bold" /></button>
      </div>
      <textarea
        ref={ref}
        className="textarea"
        style={{ minHeight, fontFamily: "'IBM Plex Sans', monospace", lineHeight: 1.6 }}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
